import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Tooltip,
  Grid,
  Accordion,
  Typography,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import TableFormDialog from 'components/popups/TableFormDialog';
import TableService from 'services/TableService';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Info from 'components/utils/Alert/Info';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { ShopContext } from 'Context/ShopContext';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import { UserContext } from 'Context/UserContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableEditDialog from 'components/popups/EditTableDialog';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AreaFormDialog from 'components/popups/AreaFormDialog';
import { useTranslation } from 'react-i18next';
import { InventoryContext } from 'Context/InventoryContext';
import QrCodeRoundedIcon from '@mui/icons-material/QrCodeRounded';
import templateImageSrc from 'images/template-qr-menu.webp';
import QRCodeStyling from 'qr-code-styling';

function Tables() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { getPriceCategories } = useContext(InventoryContext);

  const [tables, setTables] = useState(null);
  const [areas, setAreas] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [selectedTableForDelete, setSelectedTableForDelete] = useState(null);
  const [showAddTableForm, setShowAddTableForm] = useState(false);
  const [showAddAreaForm, setShowAddAreaForm] = useState(false);
  const [selectedTableForEdit, setSelectedTableForEdit] = useState(null);
  const [selectedAreaForEdit, setSelectedAreaForEdit] = useState(null);
  const [selectedAreaForDelete, setSelectedAreaForDelete] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const userHasSalesPersonWritePermission = user?.shop_permissions?.sales_persons_permission === USER_PERMISSIONS.WRITE;

  const handleDeleteClick = (tables) => setSelectedTableForDelete(tables);
  const handleAddTableClick = () => setShowAddTableForm(true);
  const handleAddAreaClick = () => setShowAddAreaForm(true);
  const handleEditTableClick = (tables) => setSelectedTableForEdit(tables);
  const handleEditAreaClick = (area) => setSelectedAreaForEdit(area);
  const handleDelateAreaClick = (area) => setSelectedAreaForDelete(area);

  const handleDownloadQr = async (table) => {
    const url = `https://cart.sanoft.com/${shop.id}?order_type=table&table=${table.name}`;

    const qrCodeOptions = {
      type: 'canvas',
      shape: 'square',
      width: 1000,
      height: 1000,
      data: url,
      margin: 0,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'Q',
      },
      dotsOptions: {
        type: 'dots',
        color: '#322c29',
        roundSize: true,
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#000000',
      },
      cornersDotOptions: {
        type: 'dot',
        color: '#000000',
      },
    };

    if (shop.shop_logo) {
      qrCodeOptions.image = shop.shop_logo;
      qrCodeOptions.imageOptions = {
        crossOrigin: 'anonymous',
        imageSize: 0.4,
        margin: 10,
        hideBackgroundDots: true,
      };
    }

    const qrCode = new QRCodeStyling(qrCodeOptions);

    const templateImage = new Image();
    templateImage.src = templateImageSrc;
    templateImage.crossOrigin = 'anonymous';

    await new Promise((res) => (templateImage.onload = res));

    const qrBlob = await qrCode.getRawData('png');
    const qrImageUrl = URL.createObjectURL(qrBlob);

    const qrImage = new Image();
    qrImage.src = qrImageUrl;

    await new Promise((res) => (qrImage.onload = res));

    const canvas = document.createElement('canvas');
    canvas.width = templateImage.width;
    canvas.height = templateImage.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(templateImage, 0, 0);

    const qrWidth = 960;
    const qrHeight = 960;
    const x = 310;
    const y = 800;

    ctx.drawImage(qrImage, x, y, qrWidth, qrHeight);

    const finalDataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = finalDataUrl;
    link.download = `${table.name}_qr_menu.png`;
    link.click();
  };

  const handleDeleteTable = async () => {
    setLoading(true);
    try {
      await TableService.deleteTables(selectedTableForDelete.id);
      setSelectedTableForDelete(null);
      postAlertMessage({ text: tManagement('Table deleted successfully'), type: 'success' });
      await getTables();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoading(false);
  };

  const handleDeleteArea = async () => {
    setLoading(true);
    try {
      await TableService.deleteArea(selectedAreaForDelete.id);
      setSelectedAreaForDelete(null);
      postAlertMessage({ text: tManagement('Area deleted successfully'), type: 'success' });
      await getAreas();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoading(false);
  };

  const getTables = useCallback(async () => {
    const tables = await TableService.getTables();
    setTables(tables);
  }, []);

  const getAreas = useCallback(async () => {
    const areas = await TableService.getAreas();
    setAreas(areas);
  }, []);

  const handleAreaSuccess = async () => {
    setSelectedAreaForEdit(null);
    setShowAddAreaForm(false);
    setSelectedAreaForEdit(null);
    try {
      await getAreas();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
  };

  const handleTableSuccess = async () => {
    setSelectedAreaForEdit(null);
    setShowAddTableForm(false);
    setSelectedTableForEdit(null);
    try {
      await getTables();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Order'),
      id: 'order',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            {userHasSalesPersonWritePermission && (
              <>
                <Tooltip title={tCommon('Edit Table')} onClick={() => handleEditTableClick(item)}>
                  <EditIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tCommon('Delete Table')} onClick={() => handleDeleteClick(item)}>
                  <DeleteIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tCommon('Download QR')} onClick={() => handleDownloadQr(item)}>
                  <QrCodeRoundedIcon className={styles.iconButton} />
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const featureIsExpired = () => {
    const currentDate = new Date();
    const validityDate = new Date(shop.kot_validity);
    return shop.kot_validity == null || currentDate > validityDate;
  };

  const getInitialData = useCallback(async () => {
    setLoading(true);
    await Promise.all([getTables(), getAreas()]);
    setLoading(false);
  }, [getTables, getAreas]);

  useEffect(() => {
    if (!shop?.id || featureIsExpired()) {
      return;
    }
    getInitialData();
    //eslint-disable-next-line
  }, [shop?.id, shop?.kot_validity]);

  useEffect(() => {
    if (subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM) {
      getPriceCategories();
    }
  }, [getPriceCategories, subscriptionType]);

  return (
    <div className={styles.contentLayout}>
      {isLoading && <CircularProgress style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 1000 }} />}
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Manage Tables')}</span>
          <div className={styles.filerInputSec}>
            {subscriptionType && shop.kot_validity !== null && areas && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ backgroundColor: '#ff851b' }}
                  className={styles.actionBtn}
                  onClick={handleAddTableClick}
                >
                  <NoteAddIcon className={styles.actionBtnIcon} />
                  {tCommon('New Table')}
                </Button>
                <Button 
                  variant="outlined" 
                  style={{ backgroundColor: '#fff', color: '#ff851b',borderColor: '#ff851b'}}
                  className={styles.actionBtn} onClick={handleAddAreaClick}>
                  <NoteAddIcon className={styles.actionBtnIcon} />
                  {tCommon('New Area')}
                </Button>
              </>
            )}
          </div>
        </div>
        {shop && featureIsExpired() ? (
          <RestrictionInfo
            title={tManagement('KOT Feature not available')}
            content={tManagement('To get KOT support options, contact support team.')}
          />
        ) : (
          <>
            {subscriptionType &&
              shop.kot_validity !== null &&
              subscriptionType !== SUBSCRIPTION_TYPE.BASIC &&
              areas &&
              (areas.length ? (
                    <>
                  <Grid container style={{ padding: '10px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '8px', fontWeight: 'bold' }}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>{tCommon('Name')}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>{tCommon('Order')}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>{tCommon('Price Category')}</Typography>
                    </Grid>
                  </Grid>
                  {areas.map((area) => (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
                      <Grid container>
                        <Grid item xs={4}>
                          <Typography variant="subtitle1" gutterBottom>
                            {area.name?.toUpperCase()}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="subtitle1" gutterBottom>
                            {area.order}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="subtitle1" gutterBottom>
                            {area.price_category?.name || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={3} style={{ display: 'flex', gap: '30px' }}>
                          <Tooltip
                            title={tCommon('Edit area')}
                            onClick={(e) => {
                              handleEditAreaClick(area);
                              e.stopPropagation();
                            }}
                          >
                            <EditIcon className={styles.iconButton} />
                          </Tooltip>
                          <Tooltip
                            title="Delete Area"
                            onClick={(e) => {
                              handleDelateAreaClick(area);
                              e.stopPropagation();
                            }}
                          >
                            <DeleteIcon className={styles.iconButton} />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails style={{ backgroundColor: '#f9f9f9', padding: 8 }}>
                      <DataTable
                        columns={headerData}
                        rows={tables?.filter((table) => table.area?.id === area.id) || []}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
                </>
              ) : (
                <Info
                  title={tManagement('No tables to list')}
                  content={tManagement(
                    'You have no table to list with current filter configuration. Please clear the filters or create a new table'
                  )}
                />
              ))}

            {showAddTableForm && (
              <TableFormDialog
                handleClose={() => setShowAddTableForm(false)}
                handleSuccess={handleTableSuccess}
                areas={areas}
              />
            )}
            {showAddAreaForm && (
              <AreaFormDialog
                handleClose={() => setShowAddAreaForm(false)}
                handleSuccess={handleAreaSuccess}
                area={null}
              />
            )}
            {selectedTableForDelete && (
              <ConfirmPopup
                data={selectedTableForDelete}
                handleClose={() => setSelectedTableForDelete(null)}
                handleOkay={handleDeleteTable}
                alertTitle={tManagement('Confirm delete')}
                successBtnName={tCommon('Delete')}
                alertContent={tManagement("Deleted table can't be restored, do you want to delete the table ? ")}
              />
            )}
            {selectedAreaForDelete && (
              <ConfirmPopup
                data={selectedAreaForDelete}
                handleClose={() => setSelectedAreaForDelete(null)}
                handleOkay={handleDeleteArea}
                alertTitle={tManagement('Confirm delete')}
                successBtnName={tCommon('Delete')}
                alertContent={tManagement("Deleted area can't be restored, do you want to delete the area ? ")}
              />
            )}
            {selectedTableForEdit && (
              <TableEditDialog
                table={selectedTableForEdit}
                handleClose={() => {
                  setSelectedTableForEdit(null);
                }}
                handleSuccess={handleTableSuccess}
                areas={areas}
              />
            )}
            {selectedAreaForEdit && (
              <AreaFormDialog
                area={selectedAreaForEdit}
                handleClose={() => {
                  setSelectedAreaForEdit(null);
                }}
                handleSuccess={handleAreaSuccess}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Tables;
